import { supabase } from './supabase';
import type { PictureCategory } from './storage';

// Interface for restaurant pictures
export interface RestaurantPicture {
  id: string;
  restaurant_id: string;
  category: PictureCategory;
  url: string;
  file_name?: string;
  file_size?: number;
  content_type?: string;
  created_at: string;
  is_valid: boolean;
}

// Function to get restaurant pictures by category
export const getRestaurantPicturesByCategory = async (restaurantId: string): Promise<{
  gallery: string[];
  menu: string[];
  interior: string[];
  exterior: string[];
}> => {
  try {
    // Use the database function to get pictures
    const { data, error } = await supabase.rpc('get_restaurant_pictures', {
      p_restaurant_id: restaurantId
    });

    if (error) throw error;

    return {
      gallery: data?.gallery || [],
      menu: data?.menu || [],
      interior: data?.interior || [],
      exterior: data?.exterior || []
    };
  } catch (err) {
    console.error('Error getting restaurant pictures:', err);
    return {
      gallery: [],
      menu: [],
      interior: [],
      exterior: []
    };
  }
};

// Function to add a picture to a restaurant
export const addRestaurantPicture = async (
  restaurantId: string,
  category: PictureCategory,
  url: string,
  fileName?: string,
  fileSize?: number,
  contentType?: string
): Promise<string> => {
  try {
    const { data, error } = await supabase.rpc('add_restaurant_picture', {
      p_restaurant_id: restaurantId,
      p_category: category,
      p_url: url,
      p_file_name: fileName,
      p_file_size: fileSize,
      p_content_type: contentType
    });

    if (error) throw error;
    return data;
  } catch (err) {
    console.error('Error adding restaurant picture:', err);
    throw err;
  }
};

// Function to remove a picture from a restaurant
export const removeRestaurantPicture = async (
  restaurantId: string,
  url: string
): Promise<boolean> => {
  try {
    const { data, error } = await supabase.rpc('remove_restaurant_picture', {
      p_restaurant_id: restaurantId,
      p_url: url
    });

    if (error) throw error;
    return data;
  } catch (err) {
    console.error('Error removing restaurant picture:', err);
    throw err;
  }
};

// Function to get all pictures for a restaurant
export const getAllRestaurantPictures = async (restaurantId: string): Promise<RestaurantPicture[]> => {
  try {
    const { data, error } = await supabase
      .from('restaurant_pictures')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .eq('is_valid', true)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('Error getting all restaurant pictures:', err);
    return [];
  }
};

// Function to get restaurant details with pictures
export const getRestaurantWithPictures = async (restaurantId: string) => {
  try {
    // Get restaurant details
    const { data: restaurant, error: restaurantError } = await supabase
      .from('restaurants')
      .select('*')
      .eq('id', restaurantId)
      .single();

    if (restaurantError) throw restaurantError;

    // Get pictures using the new function
    const pictures = await getRestaurantPicturesByCategory(restaurantId);

    return {
      ...restaurant,
      pictures
    };
  } catch (err) {
    console.error('Error getting restaurant with pictures:', err);
    throw err;
  }
};